// ===================================================================
// SHELTER — Mass Communications
// Send SMS/Email to survivors at shelters
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Button, Textarea, Select,
  Stack, SimpleGrid, MultiSelect, Progress, SegmentedControl,
} from '../../components/gab/GabComponents';
import {
  IconSend, IconMail, IconMessage, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { WORKER_URL } from '../../lib/api-switch';

const WORKER_BASE = WORKER_URL;
const ORIGIN_HEADER = 'https://apps.opengov.com';

type MessageType = 'sms' | 'email';
type TargetMode = 'shelter' | 'specific' | 'all';

interface SendResult {
  total: number;
  sent: number;
  failed: number;
  errors: string[];
}

export default function Comms() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    shelters, survivors,
    loadShelters, loadSurvivors,
  } = useShelterStore();

  const [messageType, setMessageType] = useState<MessageType>('sms');
  const [targetMode, setTargetMode] = useState<TargetMode>('shelter');
  const [selectedShelterId, setSelectedShelterId] = useState<string | null>(null);
  const [selectedSurvivorIds, setSelectedSurvivorIds] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<SendResult | null>(null);

  useEffect(() => { loadShelters(); loadSurvivors(); }, [incidentId, loadShelters, loadSurvivors]);

  // Store already server-side filters by active incident; use directly
  const iShelters = incidentId ? shelters : [];
  const iSurvivors = incidentId ? survivors : [];

  const shelterSelectData = useMemo(() => iShelters.map(s => ({ value: String(s.id), label: `${s.name} (${s.currentPopulation})` })), [iShelters]);
  const survivorSelectData = useMemo(() => iSurvivors.map(s => ({
    value: String(s.id),
    label: `${s.firstName} ${s.lastName}`,
  })), [iSurvivors]);

  // Get recipients based on target mode
  const recipients = useMemo(() => {
    if (targetMode === 'shelter' && selectedShelterId) {
      return iSurvivors.filter(s => s.shelterId === selectedShelterId && s.status === 'checked_in');
    }
    if (targetMode === 'specific') {
      return iSurvivors.filter(s => selectedSurvivorIds.includes(String(s.id)));
    }
    if (targetMode === 'all') {
      return iSurvivors.filter(s => s.status === 'checked_in');
    }
    return [];
  }, [targetMode, selectedShelterId, selectedSurvivorIds, iSurvivors]);

  const validRecipients = useMemo(() => {
    if (messageType === 'sms') return recipients.filter(r => r.phone?.trim());
    return recipients.filter(r => r.email?.trim());
  }, [recipients, messageType]);

  const handleSend = async () => {
    if (!message.trim()) {
      notifications.show({ message: 'Message is required', color: '#C68700' });
      return;
    }
    if (validRecipients.length === 0) {
      notifications.show({ message: `No recipients with valid ${messageType === 'sms' ? 'phone numbers' : 'email addresses'}`, color: '#C68700' });
      return;
    }
    if (messageType === 'email' && !subject.trim()) {
      notifications.show({ message: 'Subject is required for email', color: '#C68700' });
      return;
    }

    setSending(true);
    setProgress(0);
    setResult(null);

    const sendResult: SendResult = { total: validRecipients.length, sent: 0, failed: 0, errors: [] };

    for (let i = 0; i < validRecipients.length; i++) {
      const recipient = validRecipients[i];
      try {
        if (messageType === 'sms') {
          const resp = await fetch(`${WORKER_BASE}/api/sms`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Origin': ORIGIN_HEADER },
            body: JSON.stringify({ phone: recipient.phone, message: message }),
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        } else {
          const resp = await fetch(`${WORKER_BASE}/api/email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Origin': ORIGIN_HEADER },
            body: JSON.stringify({ to: recipient.email, subject, body: message }),
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        }
        sendResult.sent++;
      } catch (err: unknown) {
        sendResult.failed++;
        const name = `${recipient.firstName} ${recipient.lastName}`;
        sendResult.errors.push(`${name}: ${err instanceof Error ? err.message : 'Failed'}`);
      }
      setProgress(((i + 1) / validRecipients.length) * 100);
    }

    setResult(sendResult);
    setSending(false);

    if (sendResult.failed === 0) {
      notifications.show({ message: `All ${sendResult.sent} messages sent successfully`, color: 'var(--color-success)' });
    } else {
      notifications.show({ message: `${sendResult.sent} sent, ${sendResult.failed} failed`, color: '#F86A0B' });
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3} c="black">Mass Communications</Title>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          onClick={() => { loadShelters(); loadSurvivors(); }}>Refresh</Button>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Compose */}
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={700}>Compose Message</Text>

            <SegmentedControl value={messageType} onChange={v => setMessageType(v as MessageType)}
              data={[
                { value: 'sms', label: 'SMS' },
                { value: 'email', label: 'Email' },
              ]} />

            <SegmentedControl value={targetMode} onChange={v => setTargetMode(v as TargetMode)}
              data={[
                { value: 'shelter', label: 'By Shelter' },
                { value: 'specific', label: 'Specific People' },
                { value: 'all', label: 'All Survivors' },
              ]} />

            {targetMode === 'shelter' && (
              <Select label="Select Shelter" data={shelterSelectData}
                value={selectedShelterId} onChange={setSelectedShelterId} searchable />
            )}

            {targetMode === 'specific' && (
              <MultiSelect label="Select Survivors" data={survivorSelectData}
                value={selectedSurvivorIds} onChange={setSelectedSurvivorIds}
                searchable clearable maxDropdownHeight={200} />
            )}

            {messageType === 'email' && (
              <Textarea label="Subject" rows={1} value={subject}
                onChange={e => setSubject(e.target.value)} />
            )}

            <Textarea label="Message" rows={6} value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={messageType === 'sms'
                ? 'Type your SMS message here...'
                : 'Type your email body here...'
              } />

            {sending && <Progress value={progress} size="lg" animated />}

            <Button leftSection={messageType === 'sms' ? <IconMessage size={16} /> : <IconMail size={16} />}
              onClick={handleSend} loading={sending}
              disabled={!canEdit || validRecipients.length === 0 || !message.trim()}>
              <IconSend size={14} style={{ marginRight: 4 }} />
              {canEdit
                ? `Send ${messageType.toUpperCase()} to ${validRecipients.length} recipient${validRecipients.length !== 1 ? 's' : ''}`
                : 'View Only'}
            </Button>
          </Stack>
        </Paper>

        {/* Preview / Results */}
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={700}>Recipients Preview</Text>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Selected</Text>
                <Text size="xl" fw={700}>{recipients.length}</Text>
              </div>
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">Valid for {messageType.toUpperCase()}</Text>
                <Text size="xl" fw={700} c={validRecipients.length > 0 ? 'var(--color-success)' : 'var(--color-error)'}>{validRecipients.length}</Text>
              </div>
            </SimpleGrid>

            {recipients.length > 0 && recipients.length !== validRecipients.length && (
              <Text size="sm" c="#F86A0B">
                {recipients.length - validRecipients.length} recipient{recipients.length - validRecipients.length !== 1 ? 's' : ''} missing
                {messageType === 'sms' ? ' phone number' : ' email address'}
              </Text>
            )}

            {validRecipients.length > 0 && (
              <Paper p="sm" withBorder style={{ maxHeight: 200, overflow: 'auto' }}>
                <Stack gap={4}>
                  {validRecipients.slice(0, 50).map(r => (
                    <Group key={r.id} justify="space-between">
                      <Text size="sm">{r.firstName} {r.lastName}</Text>
                      <Text size="xs" c="dimmed">{messageType === 'sms' ? r.phone : r.email}</Text>
                    </Group>
                  ))}
                  {validRecipients.length > 50 && (
                    <Text size="xs" c="dimmed" ta="center">...and {validRecipients.length - 50} more</Text>
                  )}
                </Stack>
              </Paper>
            )}

            {result && (
              <Paper p="sm" withBorder bg={result.failed === 0 ? 'rgba(3,119,48,0.08)' : 'rgba(248,106,11,0.08)'}>
                <Text fw={700} mb="xs">Send Results</Text>
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <div><Text size="xs" fw={700}>Total</Text><Text fw={600}>{result.total}</Text></div>
                  <div><Text size="xs" fw={700} c="var(--color-success)">Sent</Text><Text fw={600} c="var(--color-success)">{result.sent}</Text></div>
                  <div><Text size="xs" fw={700} c="var(--color-error)">Failed</Text><Text fw={600} c="var(--color-error)">{result.failed}</Text></div>
                </SimpleGrid>
                {result.errors.length > 0 && (
                  <Stack gap={2} mt="sm">
                    <Text size="xs" fw={700} c="var(--color-error)">Errors:</Text>
                    {result.errors.map((err, idx) => (
                      <Text key={idx} size="xs" c="var(--color-error)">{err}</Text>
                    ))}
                  </Stack>
                )}
              </Paper>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}
